import { api } from "../config/api";
import * as signalR from '@microsoft/signalr';

// ─── Conexão WebSocket ──────────────────────────────────────────────────
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5181/api';
let connection = null;
let connectedUserId = null; //  NOVO — rastreia de quem é a conexão atual

export const connectToChat = (userId, token) => {
  // Reaproveita se já está conectado OU no meio do processo de conectar,
  // desde que seja o MESMO usuário — evita derrubar uma negociação em andamento.
  if (
    connection &&
    connectedUserId === userId &&
    (connection.state === signalR.HubConnectionState.Connected ||
     connection.state === signalR.HubConnectionState.Connecting)
  ) {
    return connection;
  }

  // Só derruba a conexão anterior se for de um usuário DIFERENTE,
  // ou se ela já estiver morta/desconectada de vez.
  if (connection && connectedUserId !== userId) {
    connection.stop().catch(() => {});
  }

  const baseUrl = API_URL.replace('/api', '');
  const authToken = token || localStorage.getItem('token');

  connection = new signalR.HubConnectionBuilder()
    .withUrl(`${baseUrl}/chatHub`, {
      accessTokenFactory: () => authToken,
      withCredentials: true
    })
    .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
    .configureLogging(signalR.LogLevel.Information)
    .build();

  connectedUserId = userId;

  connection.start()
    .then(() => {
      // console.log('✅ Chat conectado com sucesso para', userId);
    })
    .catch((err) => {
      // console.error('❌ Falha ao conectar ao chat:', err);
    });

  return connection;
};

export const disconnectChat = () => {
  if (connection) {
    connection.stop().catch(() => {});
    connection = null;
    connectedUserId = null;
  }
};

export const getConnection = () => connection;

export const isConnected = () => {
  return connection && connection.state === signalR.HubConnectionState.Connected;
};

// ─── Conversas ──────────────────────────────────────────────────────────
export const getConversations = async (type) => {
  try {
    const response = await api.get(`/chat/conversations`, { params: { type } });
    return response.data;
  } catch (error) {
    console.error('Erro ao carregar conversas:', error);
    throw error;
  }
};

export const getConversation = async (conversationId) => {
  try {
    const response = await api.get(`/chat/conversations/${conversationId}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao carregar conversa:', error);
    throw error;
  }
};

export const createJobConversation = async (jobId, freelancerId, companyId = null) => {
  try {
    const userId = localStorage.getItem('userId');
    const userType = localStorage.getItem('userType');
    
    let payload = { jobId };
    
    if (userType === 'Freelancer') {
      payload.freelancerId = userId;
      payload.companyId = companyId;
    } else if (userType === 'Company') {
      payload.companyId = userId;
      payload.freelancerId = freelancerId;
    } else {
      throw new Error('Tipo de usuário não suportado');
    }

    // console.log('📤 Enviando payload:', payload); // 👈 ADICIONA ISSO
    // console.log('📤 userType:', userType); // 👈 ADICIONA ISSO
    // console.log('📤 userId:', userId); // 👈 ADICIONA ISSO

    const response = await api.post('/chat/conversations/job', payload);
    return response.data;
  } catch (error) {
    // console.error('Erro ao criar conversa da vaga:', error);
    // console.error('Resposta do backend:', error.response?.data); // 👈 ADICIONA ISSO
    throw error;
  }
};

export const createSupportConversation = async (freelancerId, companyId = null, adminId = null) => {
  try {
    const userId = localStorage.getItem('userId');
    const userType = localStorage.getItem('userType');
    
    let payload = {};
    
    if (userType === 'Freelancer') { // Freelancer
      payload.freelancerId = userId;
    } else if (userType === 'Company') { // Company
      payload.companyId = userId;
    } else if (userType === 'Admin') { // Admin
      payload.adminId = userId;
      payload.freelancerId = freelancerId;
      payload.companyId = companyId;
    } else {
      throw new Error('Tipo de usuário não suportado');
    }

    const response = await api.post('/chat/conversations/support', payload);
    return response.data;
  } catch (error) {
    console.error('Erro ao criar conversa de suporte:', error);
    throw error;
  }
};

export const closeConversation = async (conversationId) => {
  try {
    const response = await api.patch(`/chat/conversations/${conversationId}/close`);
    return response.data;
  } catch (error) {
    console.error('Erro ao fechar conversa:', error);
    throw error;
  }
};

export const getUnreadCount = async () => {
  try {
    const response = await api.get('/chat/unread-count');
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar contagem de não lidas:', error);
    throw error;
  }
};

// ─── Mensagens ──────────────────────────────────────────────────────────
export const getMessages = async (conversationId, page = 1, limit = 50) => {
  try {
    const response = await api.get(`/chat/messages/${conversationId}`, {
      params: { page, limit }
    });
    return response.data;
  } catch (error) {
    console.error('Erro ao carregar mensagens:', error);
    throw error;
  }
};

export const sendMessage = async (conversationId, message, attachments = []) => {
  try {
    const userId = localStorage.getItem('userId');
    
    if (!userId) {
      throw new Error('Usuário não autenticado');
    }

    if (!conversationId) {
      throw new Error('ID da conversa é obrigatório');
    }

    const payload = {
      conversationId: conversationId,
      senderId: userId,
      message: message,
      attachmentUrl: attachments.length > 0 ? attachments[0].url : null,
      attachmentType: attachments.length > 0 ? attachments[0].type : null
    };

    // Tenta enviar via socket primeiro
    if (connection && connection.state === signalR.HubConnectionState.Connected) {
      await connection.invoke('SendMessage', payload);
      return { success: true, sentVia: 'socket' };
    }

    // Fallback para API
    const response = await api.post(`/chat/messages`, payload);
    return response.data;
  } catch (error) {
    console.error('❌ Erro ao enviar mensagem:', error);
    if (error.response) {
      console.error('Status:', error.response.status);
      console.error('Dados do erro:', error.response.data);
    }
    throw error;
  }
};

export const markAsRead = async (conversationId) => {
  try {
    const response = await api.patch(`/chat/messages/${conversationId}/read`);
    return response.data;
  } catch (error) {
    console.error('Erro ao marcar como lida:', error);
    throw error;
  }
};

// Alias — backend só tem uma rota de "marcar como lida", que já marca todas
// as mensagens não lidas da conversa de uma vez.
export const markAllAsRead = async (conversationId) => {
  return markAsRead(conversationId);
};

// ─── Upload de Arquivos ────────────────────────────────────────────────
export const uploadChatAttachment = async (file) => {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post('/chat/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  } catch (error) {
    console.error('Erro ao fazer upload do arquivo:', error);
    throw error;
  }
};

// ─── Eventos do Socket ─────────────────────────────────────────────────
export const onNewMessage = (callback) => { 
  if (connection) connection.on('new_message', callback); 
};

export const onConversationUpdated = (callback) => { 
  if (connection) connection.on('conversation_updated', callback); 
};

export const onUserConnected = (callback) => { 
  if (connection) connection.on('user_connected', callback); 
};

export const onUserDisconnected = (callback) => { 
  if (connection) connection.on('user_disconnected', callback); 
};

export const onTyping = (callback) => { 
  if (connection) connection.on('typing', callback); 
};

export const onError = (callback) => { 
  if (connection) connection.on('error', callback); 
};

// ─── Remover Eventos ──────────────────────────────────────────────────
export const offNewMessage = (callback) => { 
  if (connection) connection.off('new_message', callback); 
};

export const offConversationUpdated = (callback) => { 
  if (connection) connection.off('conversation_updated', callback); 
};

export const offTyping = (callback) => { 
  if (connection) connection.off('typing', callback); 
};

export const offError = (callback) => { 
  if (connection) connection.off('error', callback); 
};

// ─── Ações do Socket ────────────────────────────────────────────────────
export const sendTyping = async (conversationId, isTyping) => {
  try {
    if (connection && connection.state === signalR.HubConnectionState.Connected) {
      await connection.invoke('Typing', { conversationId, isTyping });
    }
  } catch (error) {
    console.error('Erro ao enviar typing:', error);
  }
};

export const joinConversation = async (conversationId) => {
  try {
    if (connection && connection.state === signalR.HubConnectionState.Connected) {
      await connection.invoke('JoinConversation', conversationId);
    }
  } catch (error) {
    console.error('Erro ao entrar na conversa:', error);
  }
};

export const leaveConversation = async (conversationId) => {
  try {
    if (connection && connection.state === signalR.HubConnectionState.Connected) {
      await connection.invoke('LeaveConversation', conversationId);
    }
  } catch (error) {
    console.error('Erro ao sair da conversa:', error);
  }
};